import type { ComponentPropsWithoutRef, ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { CodeBlock } from "@/components/code-block";
import { Screenshot } from "@/components/screenshot";
import { slugify } from "@/lib/markdown";
import { cn } from "@/lib/utils";

/**
 * A markdown document as the site renders it -- headings with anchors, tables that scroll
 * rather than overflow, code blocks with a copy button, images that say what they would show
 * until the file exists.
 *
 * The consumer is the SDK's README on the reference page, rendered from the file itself
 * rather than re-typed as JSX so that there is one source, and the page cannot describe an
 * API the package does not have.
 */

function text(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(text).join("");
  if (typeof node === "object" && "props" in node) {
    return text((node.props as { children?: ReactNode }).children);
  }
  return "";
}

function Heading({
  level,
  children,
  className,
}: {
  level: 2 | 3;
  children: ReactNode;
  className: string;
}) {
  const id = slugify(text(children));
  const Tag = level === 2 ? "h2" : "h3";
  return (
    <Tag id={id} className={cn("group scroll-mt-20", className)}>
      <a href={`#${id}`} className="no-underline hover:underline">
        {children}
      </a>
    </Tag>
  );
}

function FencedCode({ children }: { children?: ReactNode }) {
  // react-markdown hands `<pre>` one `<code className="language-x">text</code>` child.
  const child = Array.isArray(children) ? children[0] : children;
  const props =
    child && typeof child === "object" && "props" in child
      ? (child.props as { className?: string; children?: ReactNode })
      : {};
  const code = text(props.children).replace(/\n$/, "");
  const language = /language-(\w+)/.exec(props.className ?? "")?.[1];
  return <CodeBlock code={code} language={language} className="my-4" />;
}

const components = {
  h1: ({ children }: ComponentPropsWithoutRef<"h1">) => (
    <h1 className="mb-2 text-2xl font-semibold tracking-tight">{children}</h1>
  ),
  h2: ({ children }: ComponentPropsWithoutRef<"h2">) => (
    <Heading level={2} className="mt-10 mb-3 border-b pb-1.5 text-lg font-semibold first:mt-0">
      {children}
    </Heading>
  ),
  h3: ({ children }: ComponentPropsWithoutRef<"h3">) => (
    <Heading level={3} className="mt-6 mb-2 text-base font-semibold">
      {children}
    </Heading>
  ),
  p: ({ children }: ComponentPropsWithoutRef<"p">) => (
    <p className="my-3 text-sm leading-6">{children}</p>
  ),
  ul: ({ children }: ComponentPropsWithoutRef<"ul">) => (
    <ul className="my-3 list-disc space-y-1 pl-5 text-sm leading-6">{children}</ul>
  ),
  ol: ({ children }: ComponentPropsWithoutRef<"ol">) => (
    <ol className="my-3 list-decimal space-y-1 pl-5 text-sm leading-6">{children}</ol>
  ),
  li: ({ children }: ComponentPropsWithoutRef<"li">) => <li>{children}</li>,
  a: ({ href, children }: ComponentPropsWithoutRef<"a">) => {
    const external = href !== undefined && /^https?:/.test(href);
    return (
      <a
        href={href}
        className="text-primary underline-offset-4 hover:underline"
        {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      >
        {children}
      </a>
    );
  },
  strong: ({ children }: ComponentPropsWithoutRef<"strong">) => (
    <strong className="font-semibold">{children}</strong>
  ),
  pre: ({ children }: ComponentPropsWithoutRef<"pre">) => <FencedCode>{children}</FencedCode>,
  code: ({ children }: ComponentPropsWithoutRef<"code">) => (
    <code className="bg-muted rounded px-1 py-0.5 font-mono text-[0.85em]">{children}</code>
  ),
  table: ({ children }: ComponentPropsWithoutRef<"table">) => (
    <div className="my-4 overflow-x-auto rounded-md border">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }: ComponentPropsWithoutRef<"thead">) => (
    <thead className="bg-muted/60 text-left">{children}</thead>
  ),
  th: ({ children }: ComponentPropsWithoutRef<"th">) => (
    <th className="px-3 py-2 font-medium whitespace-nowrap">{children}</th>
  ),
  td: ({ children }: ComponentPropsWithoutRef<"td">) => (
    <td className="border-t px-3 py-2 align-top">{children}</td>
  ),
  img: (props: ComponentPropsWithoutRef<"img">) => <Screenshot {...props} className="my-4" />,
  hr: () => <hr className="my-8" />,
  blockquote: ({ children }: ComponentPropsWithoutRef<"blockquote">) => (
    <blockquote className="text-muted-foreground my-3 border-l-2 pl-4 italic">{children}</blockquote>
  ),
};

export function Markdown({ source, className }: { source: string; className?: string }) {
  return (
    <div className={cn("min-w-0", className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {source}
      </ReactMarkdown>
    </div>
  );
}
